import {takeEvery, put, call, select} from 'redux-saga/effects';
import {
  commonBenefitsFailed,
  REQUESTED_SUBMIT_REQUEST,
  updateServiceTransition,
  updateSessionId,
  refreshReducersToInitial,
  submitServiceRequestFailed,
  submitServiceRequestSuccess,
} from '../action';
import {
  submitServiceRequest,
  createMemberSessionOpenApi,
  fraudMonitor, clearLocalStorageAndGenerateUrl
} from '../api';
import {datadogRum} from "@datadog/browser-rum";
import {
  getAccidentQuestionResponse,
  getCommonBenefits,
  getHighestCoverageVehicle,
  getServiceInfo,
  hasPepSessionSelector,
  getPartnerDetails,
  getSchedulingEnabled,
  getServiceCoverageDetails,
} from "../selector";
import {cleanServiceRequestPayload, isScheduleService} from '../utils/serviceRequestPayloadUtils'
import CONSTANTS, {
  JJ_AND_AMC, STACK_TYPE_COMBINED, STACK_TYPE_NOT_STACKED,
  STACK_TYPE_RENTED_OR_BORROWED,
  TOW,
  TOW_SERVICE_ID
} from '../app-consts/appConstants';
import _isEmpty from "lodash/isEmpty";
import {UNABLE_TO_PROCESS_REQUEST} from "../app-consts/errorMessageConstants";
import { getLanguageOptionFlag, getOmniStreamlinedFlag } from '../features/FeatureFlags';
import { determineLocale, LOCALE_TO_LANGUAGE_MAP } from '../contexts/LocaleContext';
import { emitJourneyEvent, JOURNEY_STEP_TYPES } from '../utils/journeyMonitoring';

export function* submitServiceRequestSaga(action) {
  const commonBenefits = yield select(getCommonBenefits);
  const vehicleValue = yield select(state => state.vehicleValue);;
  const partnerDetails = yield select(getPartnerDetails);
  const serviceInfo = yield select(getServiceInfo);
  const hasPepSession = yield select(hasPepSessionSelector);
  const serviceCoverageDetails = yield select(getServiceCoverageDetails);

  const omniStreamlined = yield select(getOmniStreamlinedFlag);
  const schedulingEnabled = yield select(getSchedulingEnabled);
  const languageTypeFlag = yield select(getLanguageOptionFlag);
  const {appId, partnerCode, experience, route} = partnerDetails;
  const {benefitsEnabled, ppuEnabled} = experience;
  const serviceType = serviceInfo.service;
  const locale = determineLocale();
  const languageType = LOCALE_TO_LANGUAGE_MAP[locale];

  const payload = action.payload

  if (languageTypeFlag){
    payload.serviceRequest.languageType = languageType;
  }
  if (partnerCode === "JJ" && serviceInfo.id === TOW_SERVICE_ID) {
    const accidentQuestionResponse = yield select(getAccidentQuestionResponse);
    payload.accidentQuestionResponse = accidentQuestionResponse;
  }

  let sessionId = '';
  const values = new URLSearchParams(window.location.search);
  const urlSessionId = values.get('session');
  const denyRequest = {
    firstName: action.payload?.serviceRequest?.firstName,
    lastName: action.payload?.serviceRequest?.lastName,
    callbackNumber: action.payload?.serviceRequest?.contactNumber,
    membershipNumber: commonBenefits?.memberId,
    postfix: commonBenefits?.postFix,
    vinNumber: action.payload?.serviceRequest?.vin,
    smtpAddress: action.payload?.serviceRequest?.email,
    partnerCode: partnerCode,
    disablementAddress: action.payload?.serviceRequest?.disablementAddress?.addressLine1,
    disablementCityName: action.payload?.serviceRequest?.disablementAddress?.city,
    disablementCountryCode: action.payload?.serviceRequest?.disablementAddress?.country === 'US' ? 'USA' : '',
    disablementStateCode: action.payload?.serviceRequest?.disablementAddress?.state,
    destinationAddress: action.payload?.serviceRequest?.destinationAddress?.addressLine1,
    destinationCityName: action.payload?.serviceRequest?.destinationAddress?.city,
    destinationCountryCode: action.payload?.serviceRequest?.destinationAddress?.country === 'US' ? 'USA' : '',
    destinationStateCode: action.payload?.serviceRequest?.destinationAddress?.state
  }
  if(!_isEmpty(denyRequest) && JJ_AND_AMC.includes(partnerCode)){
    const denyResponse = yield call(fraudMonitor, {appId, denyRequest});
    if(denyResponse?.success && denyResponse?.body?.statusText === 'DENY' ){
      yield put(commonBenefitsFailed({
        message: CONSTANTS.SERVICE_REQUEST_DENIED
      }));
      yield put(updateServiceTransition(
        {
          errorMessageContent: UNABLE_TO_PROCESS_REQUEST,
          declineType: CONSTANTS.SERVICE_REQUEST_DENIED
        }));
      return;
    }
  }
  if (!payload.sessionId && !commonBenefits.noBenefits && payload.serviceRequest.loggedIn !== 'N' && urlSessionId) {
    datadogRum.addAction(`SessionID not available in request payload, Picked from url `, {
      displayCode: partnerDetails?.displayCode,
      serviceText: serviceInfo?.serviceText,
      payloadSessionId: payload?.sessionId,
      urlSessionId: urlSessionId
    });
    yield put(updateSessionId(urlSessionId))
    payload.sessionId = urlSessionId;
  }
  try {
    if (!payload.sessionId ||
      ( !omniStreamlined && (benefitsEnabled && (commonBenefits.noBenefits === true || payload.serviceRequest.loggedIn === 'N') ||
      (hasPepSession && serviceInfo.serviceText !== TOW)))
    ) {
      datadogRum.addAction(`Submit Rescue PPU`, {
        displayCode: partnerDetails?.displayCode,
        sessionId: payload?.sessionId,
        omniStreamlined: !!omniStreamlined,
        benefitsEnabled,
        noBenefits: commonBenefits?.noBenefits,
        loggedIn: payload?.serviceRequest?.loggedIn,
        hasPepSession,
        serviceText: serviceInfo?.serviceText,
      });
      const memberId =
        commonBenefits.noBenefits === true ||
        payload.serviceRequest.loggedIn === "N"
          ? ""
          : commonBenefits.memberId;
      const type = partnerDetails.ppuSessionType;
      const appId = payload.serviceRequest.appId;

      const latitude = payload.serviceRequest.disablementAddress.latitude;
      const longitude = payload.serviceRequest.disablementAddress.longitude;

      const options = {
        memberId,
        type,
        partnerCode,
        appId,
        latitude,
        longitude,
        flippedToPpu: ppuEnabled,
      };
      let sessionResponse;
      try {
        sessionResponse = yield call(
          createMemberSessionOpenApi,
          route,
          options
        );
      } catch (error) {
        let errorToDisplay =
          error.response?.data?.message || error.response?.data;
        datadogRum.addError(
          new Error(
            `Submit Service Request not called due to Session API failed with error: ${JSON.stringify(
              errorToDisplay
            )}, Action: ${JSON.stringify(action)}`
          )
        );
        yield put(submitServiceRequestFailed());
      }
      sessionId = sessionResponse.data.id;
      if (sessionId) {
        yield put(updateSessionId(sessionId));
      }
      payload.sessionId = sessionResponse.data.id;
    }

    const history = payload.history;
    delete payload.history;
    if (!omniStreamlined) {
      if(!payload.sessionId.includes("-")){
        datadogRum.addError(
          new Error(`Open API sessionID encountered. User session cleared`), {
            sessionID: payload.sessionId
          }
        );
        const url1 = clearLocalStorageAndGenerateUrl(false);
        yield put(refreshReducersToInitial());
        yield call([history, history.push], url1);
        return;
      }

      cleanServiceRequestPayload(serviceInfo.id, payload.serviceRequest);
    } else {
      if(payload.sessionId.includes("-")) {
        const url1 = clearLocalStorageAndGenerateUrl(false);
        yield put(refreshReducersToInitial());
        yield call([history, history.push], url1);
        return;
      }
    }

    const selectedVehicleIndex = vehicleValue?.selectedVehicleIndex;
    const etlMemberId = selectedVehicleIndex != null && commonBenefits?.vehicles
      ? commonBenefits?.vehicles?.[selectedVehicleIndex]?.memberId
      : null;

    let memberId = commonBenefits.memberId;
    if (partnerCode === "ETL" && selectedVehicleIndex != null && etlMemberId) {
      memberId = etlMemberId;
    }
    if (partnerCode === "ETL" && selectedVehicleIndex != null && etlMemberId && payload?.serviceRequest?.vehicles) {
      payload.serviceRequest.vehicles.memberId = etlMemberId;
    }
    // For Walmart partner, if the service has additional coverage(standard details), then we need to use the memberId from the serviceRequestPayload instead of commonBenefits.memberId
    if (serviceCoverageDetails?.additionalCoverage?.memberId) {
      memberId = payload.memberId;
    }

    payload.memberId = memberId;
    let response;

    if(partnerCode === "JJ" || partnerCode === "UV") {
      //COMBINED benefits takes precedence over using STACK_TYPE_RENTED_OR_BORROWED. If there is a memberId, then COMBINED is used
      // If they do have a covered vehicle, we set the highest covered vehicle Id on the stackedMembership object
      // If they do not have a covered vehicle, they would be pay per use and the stacking information does not apply

      //stackedTypeId of 1437 should never be changed
      const highestCoveredVehicle = yield select(getHighestCoverageVehicle);
      if ((payload?.stackedMembership?.stackedTypeId === STACK_TYPE_COMBINED ||
        payload?.stackedMembership?.stackedTypeId === STACK_TYPE_NOT_STACKED) && vehicleValue?.addedVehicleFlag && highestCoveredVehicle) {
        payload.stackedMembership.stackedVehicleId = highestCoveredVehicle.vehicleID;
        if (!payload?.stackedMembership?.stackedMemberId) { // same as payload?.stackedMembership?.stackedTypeId !== STACK_TYPE_COMBINED
          payload.stackedMembership.stackedTypeId = STACK_TYPE_RENTED_OR_BORROWED;
        }
      }
      if (payload?.stackedMembership?.stackedTypeId && !vehicleValue?.addedVehicleFlag && payload?.serviceRequest?.vehicles?.consumervehicleid) {
        if (payload.stackedMembership.stackedTypeId === STACK_TYPE_RENTED_OR_BORROWED) {
          payload.stackedMembership.stackedTypeId = STACK_TYPE_NOT_STACKED
        }
        payload.stackedMembership.stackedVehicleId = payload?.serviceRequest?.vehicles?.consumervehicleid;
      }
    }
    try {
      // Rescue submission is about to be sent to the service request API.
      emitJourneyEvent(JOURNEY_STEP_TYPES.USER_MILESTONE, "rescue.submitted", {
        milestoneName: "rescue.submitted",
        partnerCode,
        serviceType,
      });

      response = yield call(
        submitServiceRequest,
        payload,
        partnerCode,
        serviceType,
        partnerDetails.experience,
        hasPepSession,
        omniStreamlined
      );
    } catch(err) {
      // Rescue submission call to the service request API failed.
      emitJourneyEvent(JOURNEY_STEP_TYPES.API_FAILURE, "rescue.submission_failed", {
        milestoneName: "rescue.submission_failed",
        partnerCode,
        serviceType,
        status: err.response?.status,
      });

      datadogRum.addError(
        new Error(`Rescue submission failure through ${!!omniStreamlined ? 'Omni' : 'Open'} API. appId: ${payload.serviceRequest.appId}, partnerCode: ${partnerCode}.`),
        {
          status: err.response.status,
          path: err.response.data.path,
          payload: payload,
          partnerCode: partnerCode,
          serviceType: serviceType,
          experience: partnerDetails.experience,
          hasPepSession: hasPepSession,
          omniStreamlined: omniStreamlined,
          err
        }
      );

      if (
        err.response &&
        err.response.status === 400 &&
        err.response.data &&
        typeof err.response.data.message === 'string' &&
        err.response.data.message.includes("Session Expired due to older than") &&
        err.response.data.message.includes("creation date time.")
      ) {
        datadogRum.addError(
          new Error(`Omni Search API: Session Expired. appId: ${payload.serviceRequest.appId}, partnerCode: ${partnerCode}.`),
          {
            status: err.response.status,
            path: err.response.data.path,
          }
        );

        const url1 = clearLocalStorageAndGenerateUrl(true);
        yield put(refreshReducersToInitial());
        yield call([history, history.push], url1);
        return;
      }
    }

    if (response.status === 200 && isScheduleService(payload) && schedulingEnabled) {
      const requestScheduleDateTime = payload?.serviceRequest?.additionalFields?.[0]?.find(
        ({ key = "" }) => key === "requestscheduledatetime"
      )
      datadogRum.addAction("Scheduled Service Submission", {
        displayCode: partnerDetails?.displayCode,
        currentDate: new Date().toLocaleString("en-US"),
        requestScheduleDateTime
      });
    };
    if(!!omniStreamlined) {
      response.data = {
        callId: payload.sessionId,
        usageId: payload.usageId
      }
    }

    // Rescue submission to the service request API succeeded.
    emitJourneyEvent(JOURNEY_STEP_TYPES.USER_MILESTONE, "rescue.submitted_success", {
      milestoneName: "rescue.submitted_success",
      partnerCode,
      serviceType,
      callId: response.data?.callId,
      usageId: response.data?.usageId,
    });

    yield put(submitServiceRequestSuccess(response.data));
  } catch (error) {
    datadogRum.addError(
      new Error(
        `Submit Service Request API failed with error, Action: ${JSON.stringify(
          action
        )}`
      ),
      error
    );
    yield put(submitServiceRequestFailed());
  }
}

export function* watchSubmitRequestSaga() {
  yield takeEvery(REQUESTED_SUBMIT_REQUEST, submitServiceRequestSaga);
}
